import React, { useEffect, useRef, useState, useCallback } from 'react';
import * as d3 from 'd3';
import {
  DEVICE_TYPE_COLORS,
  DEVICE_ICONS,
  DEVICE_TYPE_IMAGES,
  iconForStationId,
  type Station,
} from '../../utils/stationGraphData';
import { useObjectLibrary, type ObjectLibraryItem } from '../../hooks/useObjectLibrary';
import {
  findLatestNodeDiscussionEntry,
  formatNodeBubbleText,
  isManagerAgent,
  nodeMatchesAgent,
  splitBubbleText,
  type SpeakingAgent,
  type TypingFeedEntry,
} from '../../utils/agentNodeDiscussion';

interface NodeModel {
  id: string;
  label?: string;
  group?: 'station' | 'device';
  stationId?: string;
  stationColor?: string;
  deviceType?: string;
  size?: number;
  color?: string;
  icon?: string;
  imageUrl?: string;
  x?: number;
  y?: number;
  fx?: number | null;
  fy?: number | null;
  displayName?: string | null;
  deviceDbId?: number | null;
}

interface LinkModel {
  source: string;
  target: string;
  stationId?: string;
  color?: string;
}

interface NodePosition {
  x: number;
  y: number;
  color: string;
  label: string;
}

interface GraphBounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

interface CommLinkDatum {
  sourceId: string;
  targetId: string;
}

function hexToRgbChannels(hex: string): string {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `${r} ${g} ${b}`;
}

const MIN_AUTO_FIT_SCALE = 0.45;
const MIN_USER_ZOOM_SCALE = 0.2;
const MAX_USER_ZOOM_SCALE = 4;

function resolveDeviceImage(type: string, libraryItems: ObjectLibraryItem[]): string | undefined {
  const fromLibrary = libraryItems.find((item) => item.type === type)?.preview_image;
  return fromLibrary || DEVICE_TYPE_IMAGES[type];
}

function buildData(stations: Station[], libraryItems: ObjectLibraryItem[] = []) {
  const nodes: NodeModel[] = [];
  const links: LinkModel[] = [];

  stations.forEach((s) => {
    nodes.push({
      id: `station-${s.id}`,
      label: s.name,
      group: 'station',
      stationId: s.id,
      stationColor: s.color,
      size: 32,
      color: s.color,
      icon: iconForStationId(s.id),
    });

    s.devices.forEach((dev) => {
      const nid = `device-${s.id}-${dev.type}-${dev.id}`;
      const c = DEVICE_TYPE_COLORS[dev.type] ?? '#64748b';
      nodes.push({
        id: nid,
        label: dev.id,
        group: 'device',
        stationId: s.id,
        stationColor: s.color,
        deviceType: dev.type,
        size: 15,
        color: c,
        icon: DEVICE_ICONS[dev.type] || '⚡',
        imageUrl: resolveDeviceImage(dev.type, libraryItems),
        displayName: dev.displayName ?? null,
        deviceDbId: dev.deviceDbId ?? null,
      });
      links.push({
        source: `station-${s.id}`,
        target: nid,
        stationId: s.id,
        color: s.color,
      });
    });
  });

  // Links between stations (hub connections)
  for (let i = 0; i < stations.length - 1; i++) {
    links.push({
      source: `station-${stations[i].id}`,
      target: `station-${stations[i + 1].id}`,
      color: '#334155',
    });
  }

  return { nodes, links };
}

function centerGraphView(
  svg: d3.Selection<SVGSVGElement, unknown, null, undefined>,
  zoom: d3.ZoomBehavior<SVGSVGElement, unknown>,
  nodes: NodeModel[],
  width: number,
  height: number,
) {
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) return;
  if (nodes.length === 0) return;

  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  nodes.forEach((node) => {
    if (typeof node.x !== 'number' || typeof node.y !== 'number') return;
    const r = (node.size ?? 15) + 30;
    if (node.x - r < minX) minX = node.x - r;
    if (node.y - r < minY) minY = node.y - r;
    if (node.x + r > maxX) maxX = node.x + r;
    if (node.y + r > maxY) maxY = node.y + r;
  });

  if (!Number.isFinite(minX)) return;

  centerGraphBounds(svg, zoom, { minX, minY, maxX, maxY }, width, height, MIN_AUTO_FIT_SCALE);
}

function centerGraphBounds(
  svg: d3.Selection<SVGSVGElement, unknown, null, undefined>,
  zoom: d3.ZoomBehavior<SVGSVGElement, unknown>,
  bounds: GraphBounds,
  width: number,
  height: number,
  minScale: number,
) {
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) return;

  const graphWidth = Math.max(bounds.maxX - bounds.minX, 1);
  const graphHeight = Math.max(bounds.maxY - bounds.minY, 1);
  const padding = width < 360 || height < 360 ? 32 : 60;
  const availableWidth = Math.max(width - padding * 2, width * 0.25);
  const availableHeight = Math.max(height - padding * 2, height * 0.25);
  const fitScale = Math.min(availableWidth / graphWidth, availableHeight / graphHeight, 1);
  const scale = Math.min(Math.max(fitScale, minScale), MAX_USER_ZOOM_SCALE);
  const tx = (width - graphWidth * scale) / 2 - bounds.minX * scale;
  const ty = (height - graphHeight * scale) / 2 - bounds.minY * scale;

  svg.call(zoom.transform, d3.zoomIdentity.translate(tx, ty).scale(scale));
}

function centerRenderedGraphView(
  svg: d3.Selection<SVGSVGElement, unknown, null, undefined>,
  zoom: d3.ZoomBehavior<SVGSVGElement, unknown>,
  width: number,
  height: number,
) {
  const viewport = svg.select<SVGGElement>('g.viewport').node();
  if (!viewport) return false;

  try {
    const bbox = viewport.getBBox();
    if (!Number.isFinite(bbox.width) || !Number.isFinite(bbox.height) || bbox.width <= 0 || bbox.height <= 0) {
      return false;
    }

    centerGraphBounds(
      svg,
      zoom,
      {
        minX: bbox.x,
        minY: bbox.y,
        maxX: bbox.x + bbox.width,
        maxY: bbox.y + bbox.height,
      },
      width,
      height,
      MIN_USER_ZOOM_SCALE,
    );
    return true;
  } catch {
    return false;
  }
}

function updateRenderedGraphPositions(svg: d3.Selection<SVGSVGElement, unknown, null, undefined>) {
  svg.selectAll<SVGLineElement, LinkModel>('g.links line')
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .attr('x1', (d: any) => d.source?.x ?? 0)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .attr('y1', (d: any) => d.source?.y ?? 0)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .attr('x2', (d: any) => d.target?.x ?? 0)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .attr('y2', (d: any) => d.target?.y ?? 0);
  svg.selectAll<SVGGElement, NodeModel>('.node')
    .attr('transform', (d) => `translate(${d.x ?? 0}, ${d.y ?? 0})`);

  const nodesById = new Map<string, NodeModel>();
  svg.selectAll<SVGGElement, NodeModel>('.node').each((d) => {
    nodesById.set(d.id, d);
  });
  svg.selectAll<SVGLineElement, CommLinkDatum | undefined>('g.comm-links line')
    .attr('x1', (d) => nodesById.get(d?.sourceId ?? '')?.x ?? 0)
    .attr('y1', (d) => nodesById.get(d?.sourceId ?? '')?.y ?? 0)
    .attr('x2', (d) => nodesById.get(d?.targetId ?? '')?.x ?? 0)
    .attr('y2', (d) => nodesById.get(d?.targetId ?? '')?.y ?? 0);
}

function renderNodeSpeechBubble(
  nodeGroup: d3.Selection<SVGGElement, NodeModel, null, undefined>,
  node: NodeModel,
  entry?: TypingFeedEntry,
) {
  // Remove existing bubble attached to this specific node
  nodeGroup.selectAll('g.speech-bubble').remove();
  if (!entry) return;

  const nodeR = node.size ?? 15;
  const color = node.color ?? '#a855f7';
  const width = node.group === 'station' ? 200 : 170;
  const lines = splitBubbleText(formatNodeBubbleText(entry.content, 90), node.group === 'station' ? 36 : 30);
  if (lines.length === 0) return;

  const lineHeight = 12;
  const paddingY = 10;
  const bubbleHeight = lines.length * lineHeight + paddingY * 2;

  // Position bubble ABOVE the node so the triangle touches the node top
  const bubbleX = -width / 2;
  const bubbleY = -(nodeR + bubbleHeight + 8);

  const bubble = nodeGroup
    .append('g')
    .attr('class', 'speech-bubble')
    .attr('transform', `translate(${bubbleX}, ${bubbleY})`)
    .style('pointer-events', 'none');

  const strokeColor = node.group === 'station' ? color : '#ffffff';
  const bgColor = node.group === 'station' ? color : '#1e293b';

  // Background rounded rect
  bubble.append('rect')
    .attr('width', width)
    .attr('height', bubbleHeight)
    .attr('rx', 8)
    .attr('ry', 8)
    .attr('fill', bgColor)
    .attr('opacity', 0.95)
    .attr('stroke', strokeColor)
    .attr('stroke-width', 2)
    .style('filter', `drop-shadow(0 2px 8px ${color}55)`);

  // Triangle pointer (pointing DOWN to the node)
  const cx = width / 2;
  const triH = 7;
  bubble.append('polygon')
    .attr('points', `${cx - 7},${bubbleHeight} ${cx + 7},${bubbleHeight} ${cx},${bubbleHeight + triH}`)
    .attr('fill', bgColor)
    .attr('opacity', 0.95);
  bubble.append('polygon')
    .attr('points', `${cx - 7},${bubbleHeight} ${cx + 7},${bubbleHeight} ${cx},${bubbleHeight + triH}`)
    .attr('fill', 'none')
    .attr('stroke', strokeColor)
    .attr('stroke-width', 2);

  // Text
  const text = bubble.append('text')
    .attr('x', width / 2)
    .attr('y', paddingY + lineHeight - 2)
    .attr('text-anchor', 'middle')
    .attr('fill', '#ffffff')
    .attr('font-size', node.group === 'station' ? 10 : 9)
    .attr('font-family', 'Inter, system-ui, sans-serif')
    .attr('font-weight', 600);

  lines.forEach((line, index) => {
    text.append('tspan')
      .attr('x', width / 2)
      .attr('dy', index === 0 ? 0 : lineHeight)
      .text(line);
  });
}

export interface AgentGraphViewProps {
  stations: Station[];
  stationsLoading?: boolean;
  stationsError?: string | null;
  selectedStationId?: string | null;
  onStationSelect?: (stationId: string) => void;
  nodeMessages?: TypingFeedEntry[];
  activeSpeakers?: SpeakingAgent[];
  isProcessing?: boolean;
  className?: string;
}

export default function AgentGraphView({
  stations,
  stationsLoading = false,
  stationsError = null,
  selectedStationId,
  onStationSelect,
  nodeMessages = [],
  activeSpeakers = [],
  isProcessing = false,
  className = '',
}: AgentGraphViewProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const zoomRef = useRef<d3.ZoomBehavior<SVGSVGElement, unknown> | null>(null);
  const nodePositionsRef = useRef<Map<string, NodePosition>>(new Map());
  const initialNodePositionsRef = useRef<Map<string, { x: number; y: number }>>(new Map());
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const simulationRef = useRef<d3.Simulation<NodeModel, any> | null>(null);
  const simulationNodesRef = useRef<NodeModel[]>([]);

  const { items: libraryItems, settled: librarySettled } = useObjectLibrary();
  const [selected, setSelected] = useState<string | null>(null);
  const selectedRef = useRef<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const [devicePopup, setDevicePopup] = useState<{
    label: string;
    displayName: string | null;
    deviceType: string | null;
    stationId: string | null;
    color: string | null;
  } | null>(null);
  const [graphReadyTick, setGraphReadyTick] = useState(0);

  // Highlight selected station node when selectedStationId changes from parent
  useEffect(() => {
    if (!svgRef.current) return;
    const svg = d3.select(svgRef.current);
    svg.selectAll<SVGGElement, NodeModel>('.node').each(function (d) {
      const isSelected = selectedStationId && (d.stationId === selectedStationId || d.id === `station-${selectedStationId}`);
      d3.select(this)
        .select('.main-circle')
        .attr('stroke-width', isSelected ? (d.group === 'station' ? 7 : 4) : (d.group === 'station' ? 4 : 3))
        .style('filter', isSelected
          ? `drop-shadow(0 0 18px ${d.color}) drop-shadow(0 0 8px ${d.color})`
          : d.group === 'station'
          ? `drop-shadow(0 0 12px ${d.color})`
          : 'none');
    });
  }, [selectedStationId]);

  useEffect(() => {
    if (!svgRef.current) return;
    const svg = d3.select(svgRef.current);
    const viewport = svg.select<SVGGElement>('g.viewport');
    if (viewport.empty()) return;

    const hasManagerSpeaker = activeSpeakers.some(
      (s) => isManagerAgent(s),
    );

    // ── Pulse ring + glow per node ──────────────────────────────────────
    svg.selectAll<SVGGElement, NodeModel>('.node').each(function (d) {
      const nodeGroup = d3.select<SVGGElement, NodeModel>(this);
      const latestDiscussion = findLatestNodeDiscussionEntry(d, nodeMessages);
      const nodeHasActiveSpeaker = activeSpeakers.some(
        (speaker) => speaker.status !== 'done' && nodeMatchesAgent(d, speaker),
      );

      const isSpeaking =
        (d.group === 'station' && (hasManagerSpeaker || isProcessing || Boolean(latestDiscussion))) ||
        (d.group === 'device' && (nodeHasActiveSpeaker || Boolean(latestDiscussion)));

      nodeGroup.select('.main-circle')
        .attr('stroke-width', isSpeaking
          ? (d.group === 'station' ? 6 : 5)
          : (d.group === 'station' ? 4 : 3))
        .style('filter', isSpeaking
          ? `drop-shadow(0 0 28px ${d.color}) drop-shadow(0 0 14px ${d.color})`
          : d.group === 'station'
            ? `drop-shadow(0 0 12px ${d.color})`
            : 'none');

      const ring = nodeGroup.select<SVGCircleElement>('.pulse-ring');
      ring.interrupt();

      if (isSpeaking) {
        const baseR = d.size ?? 10;
        const maxR = baseR + (d.group === 'station' ? 24 : 14);
        nodeGroup.classed('node-is-speaking', true);
        const doPulse = () => {
          if (!nodeGroup.classed('node-is-speaking')) return;
          ring
            .attr('r', baseR)
            .attr('stroke-opacity', 0.75)
            .transition().duration(1400).ease(d3.easeCubicOut)
            .attr('r', maxR).attr('stroke-opacity', 0)
            .on('end', doPulse);
        };
        doPulse();
      } else {
        nodeGroup.classed('node-is-speaking', false);
        ring.attr('stroke-opacity', 0).attr('r', d.size ?? 10);
      }

      renderNodeSpeechBubble(nodeGroup, d, latestDiscussion);
    });

    // ── Animated communication edges ──────────────────────────────────
    {
      let commGroup = viewport.select<SVGGElement>('g.comm-links');
      if (commGroup.empty()) {
        viewport.insert('g', 'g.nodes').attr('class', 'comm-links');
        commGroup = viewport.select<SVGGElement>('g.comm-links');
      }
      commGroup.selectAll('*').remove();

      const activeDeviceAgents = activeSpeakers.filter((s) => s.status !== 'done');

      if (isProcessing || activeDeviceAgents.length > 0 || nodeMessages.length > 0) {
        const simNodes = simulationNodesRef.current;
        simNodes.forEach((d) => {
          if (d.group !== 'device') return;
          if (typeof d.x !== 'number' || typeof d.y !== 'number') return;
          const isActive =
            activeDeviceAgents.some((speaker) => nodeMatchesAgent(d, speaker)) ||
            Boolean(findLatestNodeDiscussionEntry(d, nodeMessages));
          if (!isProcessing && !isActive) return;

          const stationNode = simNodes.find((n) => n.id === `station-${d.stationId}`);
          if (!stationNode || typeof stationNode.x !== 'number' || typeof stationNode.y !== 'number') return;

          commGroup.append('line')
            .datum({ sourceId: stationNode.id, targetId: d.id })
            .attr('x1', stationNode.x).attr('y1', stationNode.y)
            .attr('x2', d.x).attr('y2', d.y)
            .attr('stroke', isActive ? (d.color ?? '#a855f7') : (d.stationColor ?? '#a855f7'))
            .attr('stroke-width', isActive ? 2.5 : 1.2)
            .attr('stroke-opacity', isActive ? 0.9 : 0.25)
            .attr('stroke-dasharray', isActive ? '8 5' : '4 8')
            .attr('class', isActive ? 'comm-anim-active' : 'comm-anim-idle');
        });
      }
    }

  }, [activeSpeakers, isProcessing, nodeMessages, graphReadyTick]);

  useEffect(() => {
    if (!containerRef.current || stationsLoading) return;
    // Wait for the object-library fetch to settle so devices render with their
    // images on first paint instead of flashing emoji fallbacks.
    if (!librarySettled) return;
    if (stations.length === 0) { setIsLoading(false); return; }

    const container = containerRef.current;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let sim: d3.Simulation<NodeModel, any> | null = null;
    let initialized = false;
    let lastW = 0, lastH = 0;

    const init = (W: number, H: number) => {
      if (initialized) return;
      initialized = true;
      lastW = W; lastH = H;

      d3.select(container).selectAll('*').remove();

      const svg = d3
        .select(container)
        .append('svg')
        .attr('width', W)
        .attr('height', H)
        .attr('viewBox', [0, 0, W, H])
        .attr('style', 'background: transparent; cursor: grab');

      svgRef.current = svg.node();

      const g = svg.append('g').attr('class', 'viewport');

      const zoom = d3.zoom<SVGSVGElement, unknown>()
        .scaleExtent([MIN_USER_ZOOM_SCALE, MAX_USER_ZOOM_SCALE])
        .on('zoom', (event) => { g.attr('transform', event.transform); });
      zoomRef.current = zoom;
      svg.call(zoom);

      const { nodes, links } = buildData(stations, libraryItems);
      simulationNodesRef.current = nodes;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      sim = d3.forceSimulation<NodeModel>(nodes as any)
        .force('link', d3.forceLink(links)
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .id((d: any) => d.id)
          .distance((d) => ((d as LinkModel).stationId ? 86 : 150))
          .strength((d) => ((d as LinkModel).stationId ? 0.55 : 0.35))
        )
        .force('charge', d3.forceManyBody<NodeModel>().strength((d) => {
          if (d.group === 'station') return -380;
          return -55;
        }).distanceMax(260))
        .force('center', d3.forceCenter(W / 2, H / 2).strength(0.08))
        .force('collide', d3.forceCollide<NodeModel>()
          .radius((d) => (d.size ?? 15) + 14)
          .strength(0.75)
          .iterations(2)
        )
        .alpha(1)
        .alphaDecay(0.035)
        .velocityDecay(0.35);

      simulationRef.current = sim;
      sim.tick(90);
      sim.stop();
      initialNodePositionsRef.current = new Map(
        nodes
          .filter((node): node is NodeModel & { x: number; y: number } =>
            typeof node.x === 'number' && typeof node.y === 'number')
          .map((node) => [node.id, { x: node.x, y: node.y }]),
      );
      centerGraphView(svg, zoom, nodes, W, H);

      const linkElements = g.append('g').attr('class', 'links')
        .selectAll('line')
        .data(links)
        .join('line')
        .attr('stroke', (d) => `${d.color}55`)
        .attr('stroke-width', 4.5)
        .attr('stroke-opacity', 0.8);

      const nodeGroups = g.append('g').attr('class', 'nodes')
        .selectAll('g')
        .data(nodes)
        .join('g')
        .attr('class', 'node')
        .attr('role', 'button')
        .attr('tabindex', '0')
        .style('cursor', 'pointer')
        .style('outline', 'none');

      const dragBehavior = d3.drag<SVGGElement, NodeModel>()
        .on('start', (event, d) => {
          if (!event.active) sim!.alphaTarget(0.18).restart();
          d.fx = d.x;
          d.fy = d.y;
        })
        .on('drag', (event, d) => {
          d.fx = event.x;
          d.fy = event.y;
        })
        .on('end', (event, d) => {
          if (!event.active) sim!.alphaTarget(0);
          d.fx = d.x;
          d.fy = d.y;
        });

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      nodeGroups.call(dragBehavior as any);

      nodeGroups.on('dblclick', (event, d) => {
        event.stopPropagation();
        d.fx = null;
        d.fy = null;
        sim!.alpha(0.3).alphaTarget(0).restart();
      });

      // Pulse ring (rendered first = behind main circle)
      nodeGroups.append('circle')
        .attr('class', 'pulse-ring')
        .attr('r', (d) => d.size ?? 10)
        .attr('fill', 'none')
        .attr('stroke', (d) => d.color ?? '#64748b')
        .attr('stroke-width', 2)
        .attr('stroke-opacity', 0)
        .style('pointer-events', 'none');

      nodeGroups.append('circle')
        .attr('class', 'main-circle')
        .attr('r', (d) => d.size ?? 10)
        .attr('fill', (d) => (d.color ? `${d.color}20` : '#334155'))
        .attr('stroke', (d) => d.color ?? '#64748b')
        .attr('stroke-width', (d) => (d.group === 'station' ? 4 : 3))
        .style('filter', (d) => d.group === 'station' ? `drop-shadow(0 0 12px ${d.color})` : 'none')
        .style('transition', 'fill-opacity 0.3s, stroke-opacity 0.3s');

      nodeGroups
        .filter((d) => Boolean(d.imageUrl))
        .append('image')
        .attr('class', 'node-image')
        .attr('href', (d) => d.imageUrl ?? '')
        .attr('x', (d) => -Math.max((d.size ?? 10) - 4, 1))
        .attr('y', (d) => -Math.max((d.size ?? 10) - 4, 1))
        .attr('width', (d) => Math.max((d.size ?? 10) - 4, 1) * 2)
        .attr('height', (d) => Math.max((d.size ?? 10) - 4, 1) * 2)
        .attr('preserveAspectRatio', 'xMidYMid meet')
        .style('pointer-events', 'none')
        .on('error', function () {
          d3.select(this).attr('display', 'none');
          const parent = this.parentNode as SVGGElement | null;
          if (parent) d3.select(parent).select<SVGTextElement>('.node-icon').attr('opacity', 1);
        });

      nodeGroups.append('text')
        .attr('class', 'node-icon')
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'central')
        .attr('font-size', (d) => (d.group === 'station' ? 26 : 12))
        .attr('fill', '#ffffff')
        .attr('pointer-events', 'none')
        .attr('opacity', (d) => (d.imageUrl ? 0 : 1))
        .style('font-family', '"Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji",sans-serif')
        .text((d) => d.icon ?? '');

      nodeGroups.append('text')
        .attr('dy', (d) => (d.group === 'station' ? 45 : 22))
        .attr('text-anchor', 'middle')
        .attr('fill', (d) => d.color ?? '#94a3b8')
        .attr('font-size', (d) => (d.group === 'station' ? 11 : 9))
        .attr('font-weight', (d) => (d.group === 'station' ? 700 : 400))
        .attr('font-family', 'Inter, sans-serif')
        .attr('pointer-events', 'none')
        .attr('class', 'node-label')
        .text((d) => d.label ?? '');

      nodeGroups.append('text')
        .attr('class', 'display-name-label')
        .attr('dy', (d) => (d.group === 'station' ? 60 : 34))
        .attr('text-anchor', 'middle')
        .attr('fill', (d) => d.color ?? '#94a3b8')
        .attr('font-size', 8)
        .attr('font-weight', 300)
        .attr('font-family', 'Inter, sans-serif')
        .attr('pointer-events', 'none')
        .attr('opacity', (d) => (d.displayName ? 0.8 : 0))
        .text((d) => d.displayName ?? '');

      nodeGroups
        .on('mouseenter', (event, d) => {
          d3.select(event.currentTarget as SVGGElement)
            .select('.main-circle')
            .interrupt()
            .transition().duration(160).ease(d3.easeCubicOut)
            .attr('stroke-width', d.group === 'station' ? 6 : 4)
            .attr('r', (d.size ?? 10) * 1.08);
        })
        .on('mouseleave', (event, d) => {
          const isSelected = selectedStationId &&
            (d.stationId === selectedStationId || d.id === `station-${selectedStationId}`);
          d3.select(event.currentTarget as SVGGElement)
            .select('.main-circle')
            .interrupt()
            .transition().duration(220).ease(d3.easeCubicOut)
            .attr('stroke-width', isSelected ? (d.group === 'station' ? 7 : 4) : (d.group === 'station' ? 4 : 3))
            .attr('r', d.size ?? 10);
        })
        .on('click', (event, d) => {
          event.stopPropagation();
          if (d.group === 'device') {
            setDevicePopup({
              label: d.label ?? d.id,
              displayName: d.displayName ?? null,
              deviceType: d.deviceType ?? null,
              stationId: d.stationId ?? null,
              color: d.color ?? null,
            });
            return;
          }
          const sid = d.stationId ?? d.id.replace(/^station-/, '');
          onStationSelect?.(sid);
        })
        .on('keydown', (event, d) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            if (d.group === 'device') {
              setDevicePopup({
                label: d.label ?? d.id,
                displayName: d.displayName ?? null,
                deviceType: d.deviceType ?? null,
                stationId: d.stationId ?? null,
                color: d.color ?? null,
              });
              return;
            }
            const sid = d.stationId ?? d.id.replace(/^station-/, '');
            onStationSelect?.(sid);
          }
        });

      svg.on('click', () => {
        selectedRef.current = null;
        setSelected(null);
        applyStationFilter(null);
        setDevicePopup(null);
      });

      function applyStationFilter(stationId: string | null) {
        nodeGroups.style('opacity', (d) => {
          if (!stationId) return 1;
          return d.id === `station-${stationId}` || d.stationId === stationId ? 1 : 0.12;
        });
        linkElements.style('opacity', (d) => {
          if (!stationId) return 0.8;
          return (d as LinkModel).stationId === stationId ? 0.8 : 0.05;
        });
      }

      function updateGraphPositions() {
        linkElements
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .attr('x1', (d: any) => d.source?.x ?? 0)
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .attr('y1', (d: any) => d.source?.y ?? 0)
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .attr('x2', (d: any) => d.target?.x ?? 0)
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .attr('y2', (d: any) => d.target?.y ?? 0);
        nodeGroups.attr('transform', (d) => `translate(${d.x ?? 0}, ${d.y ?? 0})`);
        nodes.forEach((n) => {
          if (typeof n.x !== 'number' || typeof n.y !== 'number') return;
          nodePositionsRef.current.set(n.id, { x: n.x, y: n.y, color: n.color ?? '#60a5fa', label: n.label ?? n.id });
        });
      }

      sim.on('tick', updateGraphPositions);
      updateGraphPositions();
      sim.alpha(0.35).alphaTarget(0).restart();

      setIsLoading(false);
      setGraphReadyTick((tick) => tick + 1);
    }; // end init

    // Single ResizeObserver handles both first-paint init and subsequent resizes
    const ro = new ResizeObserver((entries) => {
      if (!entries.length) return;
      const { width, height } = entries[0].contentRect;
      if (!initialized) {
        // Wait until the panel CSS transition gives us valid dimensions
        if (width > 50 && height > 50) init(width, height);
        return;
      }
      if (Math.abs(width - lastW) < 1 && Math.abs(height - lastH) < 1) return;
      lastW = width; lastH = height;
      if (!svgRef.current || !zoomRef.current) return;
      const svg = d3.select(svgRef.current);
      svg.attr('width', width).attr('height', height).attr('viewBox', [0, 0, width, height] as unknown as string);
      sim?.force('center', d3.forceCenter(width / 2, height / 2).strength(0.08));
      sim?.alpha(0.22).alphaTarget(0).restart();
      centerGraphView(svg, zoomRef.current, simulationNodesRef.current, width, height);
    });
    ro.observe(container);

    // If container already has valid size (e.g. page reload), init immediately
    const { clientWidth: W0, clientHeight: H0 } = container;
    if (W0 > 50 && H0 > 50) init(W0, H0);

    return () => {
      sim?.stop();
      ro.disconnect();
      d3.select(container).selectAll('*').remove();
      if (simulationRef.current === sim) simulationRef.current = null;
      if (svgRef.current && !container.contains(svgRef.current)) svgRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stations, stationsLoading, libraryItems, librarySettled]);

  const handlePopupClose = useCallback(() => {
    setDevicePopup(null);
  }, []);

  const handlePillClick = useCallback((sid: string | null) => {
    const next = sid === selectedRef.current ? null : sid;
    selectedRef.current = next;
    setSelected(next);
    const svgEl = containerRef.current?.querySelector('svg');
    if (!svgEl) return;
    d3.select(svgEl).selectAll<SVGGElement, NodeModel>('.node').style('opacity', (d) => {
      if (!next) return 1;
      return d.id === `station-${next}` || d.stationId === next ? 1 : 0.12;
    });
    d3.select(svgEl).selectAll<SVGLineElement, LinkModel>('line').style('opacity', (d) => {
      if (!next) return 0.8;
      return d.stationId === next ? 0.8 : 0.05;
    });
  }, []);

  const handleResetLayout = useCallback(() => {
    const sim = simulationRef.current;
    const simNodes = simulationNodesRef.current;
    const svgEl = svgRef.current;
    const zoom = zoomRef.current;
    const container = containerRef.current;
    if (!sim || simNodes.length === 0) return;
    sim.stop();
    simNodes.forEach((n) => {
      const initialPosition = initialNodePositionsRef.current.get(n.id);
      if (!initialPosition) return;
      n.x = initialPosition.x;
      n.y = initialPosition.y;
      n.fx = null;
      n.fy = null;
    });
    const svgSelection = svgEl ? d3.select(svgEl) : null;
    if (svgSelection) {
      updateRenderedGraphPositions(svgSelection);
    }
    if (svgEl && zoom && container) {
      const centerCurrentRender = () => {
        if (!svgRef.current || !zoomRef.current || !containerRef.current) return;
        const { width, height } = containerRef.current.getBoundingClientRect();
        const svg = d3.select(svgRef.current);
        const didCenterRenderedBounds = centerRenderedGraphView(svg, zoomRef.current, width, height);
        if (!didCenterRenderedBounds) {
          centerGraphView(svg, zoomRef.current, simulationNodesRef.current, width, height);
        }
      };
      centerCurrentRender();
      window.requestAnimationFrame(centerCurrentRender);
    }
  }, []);

  const stationFilters = stations.map((s) => ({ id: s.id, name: s.name, color: s.color }));

  return (
    <div className={`relative w-full h-full overflow-hidden bg-app-bg border-l border-app-border ${className}`}>
      <div className="absolute top-2 left-2 right-2 flex gap-1.5 flex-wrap z-10 items-center">
        {selected && (
          <button
            type="button"
            onClick={() => handlePillClick(null)}
            className="px-2.5 py-1 rounded-full text-[11px] border border-app-border bg-app-surface text-app-text-sec hover:text-app-text cursor-pointer"
          >
            ✕ Clear
          </button>
        )}
        {stationFilters.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => handlePillClick(s.id)}
            style={{ '--c': hexToRgbChannels(s.color) } as React.CSSProperties}
            className={[
              'px-2.5 py-1 rounded-full text-[11px] cursor-pointer transition-all duration-150 border',
              selected === s.id
                ? '[border-color:rgb(var(--c))] [background:rgb(var(--c)_/_0.2)] [color:rgb(var(--c))] font-bold'
                : 'border-app-border bg-app-surface text-app-text-sec font-normal hover:text-app-text',
            ].join(' ')}
          >
            {s.name}
          </button>
        ))}
        <button
          type="button"
          onClick={handleResetLayout}
          title="Reset layout"
          className="ml-auto px-2.5 py-1 rounded-full text-[11px] border border-app-border bg-app-surface text-app-text-dim hover:text-app-text cursor-pointer"
        >
          ⟳ Reset
        </button>
      </div>

      {(isLoading || stationsLoading) && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-app-text-sec text-sm">
          <div className="w-8 h-8 rounded-full border-[3px] border-app-border border-t-app-accent animate-spin" />
          <span>{stationsLoading ? 'Loading stations…' : 'Building graph…'}</span>
        </div>
      )}

      {!stationsLoading && stationsError && stations.length > 0 && (
        <div className="absolute top-11 left-1/2 -translate-x-1/2 z-[60] px-3 py-1 rounded-md border border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300 text-[11px] font-semibold">
          {stationsError}
        </div>
      )}

      {!stationsLoading && stations.length === 0 && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-app-text-sec">
          <strong className="text-sm text-app-text">No stations found</strong>
          <span className="text-xs text-app-text-dim">Check inventory service / org filter</span>
        </div>
      )}

      {/* D3 canvas */}
      <div ref={containerRef} className="w-full h-full" />

      {devicePopup && devicePopup.stationId && (
        <div
          className="absolute z-20 flex flex-col gap-2 p-4 rounded-xl border border-app-border bg-app-surface shadow-lg"
          style={{
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            minWidth: 240,
            maxWidth: 300,
          }}
          role="dialog"
          aria-label={`Device ${devicePopup.label}`}
        >
          <div className="flex items-center gap-2">
            <div
              className="w-3 h-3 rounded-full shrink-0"
              style={{ backgroundColor: devicePopup.color ?? '#94a3b8' }}
            />
            <p className="text-[11px] text-app-text-dim uppercase tracking-wide flex-1">
              {devicePopup.deviceType ?? 'device'} · {devicePopup.stationId}
            </p>
            <button
              type="button"
              onClick={handlePopupClose}
              className="text-app-text-dim hover:text-app-text text-sm"
              aria-label="Close"
            >
              ✕
            </button>
          </div>
          <p className="text-sm font-semibold text-app-text">{devicePopup.label}</p>
          {devicePopup.displayName ? (
            <p className="text-sm text-app-text-sec">{devicePopup.displayName}</p>
          ) : (
            <p className="text-sm text-app-text-dim italic">No display name</p>
          )}
        </div>
      )}

      {/* SVG-only animations (stroke-dashoffset cannot be expressed in Tailwind) */}
      <style>{`
        @keyframes dash-flow-active { from { stroke-dashoffset: 0; } to { stroke-dashoffset: -26; } }
        @keyframes dash-flow-idle   { from { stroke-dashoffset: 0; } to { stroke-dashoffset: -24; } }
        .comm-anim-active { animation: dash-flow-active 0.65s linear infinite; }
        .comm-anim-idle   { animation: dash-flow-idle   1.4s  linear infinite; }
      `}</style>
    </div>
  );
}
